import { Tag } from "antd"
import dayjs from "dayjs"
import isoWeek from "dayjs/plugin/isoWeek"
import { useContext, useEffect, useMemo, useState } from "react"
import { useLocation } from "react-router-dom"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronDown, ChevronUp, CircleCheck, CircleX, ListTodo, Trash } from "lucide-react"
import { GeneralQueryKeys, OBJECT_TOUR_TASKS_HASH } from "../../../api/constants"
import { P } from "../../../auth/permissions"
import { useAbility } from "../../../components/contexts/AbilityContext"
import { ModalContext } from "../../../components/contexts/ModalContext"
import BaseButton from "../../../components/elements/BaseButton"
import BaseModal from "../../../components/layout/BaseModal"
import TourDeletePopover from "../../Touren/components/TourDeletePopover"
import TourRejectPopover from "../../Touren/components/TourRejectPopover"
import { getTourTasks } from "../../Touren/api/apiCalls"
import { useDeleteTourTasksForObjectMutation, useUpdateTourObjectTaskMutation } from "../../Touren/api/services"
import { groupObjectTaskSeries, ObjectTaskGroup, partitionObjectTasks, weekLabel } from "../../Touren/data/tourTasks"
import { TASK_STATUS_COLOR, TASK_STATUS_LABEL, TourObjectTask } from "../../Touren/types"

const DELETE_ALL_TASKS_MODAL_ID = 12
const taskIconClass = "w-4 h-4 shrink-0 text-gray-400"
const taskActionIconClass = `${taskIconClass} cursor-pointer`

dayjs.extend(isoWeek)

type ObjectTourTasksPanelType = {
    objectId: string
}

export default function ObjectTourTasksPanel(props: ObjectTourTasksPanelType) {
    const ability = useAbility()
    const location = useLocation()
    const modalCon = useContext(ModalContext)
    const canManage = ability.can(P.termine_management.action, P.termine_management.subject)
    const queryClient = useQueryClient()
    const { data, isLoading } = useQuery({
        queryKey: [GeneralQueryKeys.TOUR_TASKS + props.objectId],
        queryFn: () => getTourTasks({ objectId: props.objectId }),
        enabled: !!props.objectId,
    })
    const { mutate: updateTask } = useUpdateTourObjectTaskMutation(queryClient)
    const { mutate: deleteAllTasks } = useDeleteTourTasksForObjectMutation(queryClient)
    const currentYear = dayjs().isoWeekYear()
    const currentWeek = dayjs().isoWeek()
    const { dueNow, upcoming } = useMemo(
        () => partitionObjectTasks(data ?? [], { year: currentYear, week: currentWeek }),
        [data, currentYear, currentWeek],
    )
    const groups = useMemo(() => groupObjectTaskSeries(dueNow, upcoming), [dueNow, upcoming])
    const hasTasks = (data ?? []).length > 0

    useEffect(() => {
        if (location.hash !== `#${OBJECT_TOUR_TASKS_HASH}`) return
        document.getElementById(OBJECT_TOUR_TASKS_HASH)?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, [location.hash, isLoading, data])

    return (
        <div id={OBJECT_TOUR_TASKS_HASH} className="h-fit w-full flex flex-col mt-2 pt-2 scroll-mt-4">
            <div className="flex items-center justify-between gap-2">
                <p className="font-bold">Aufgaben</p>
                {canManage && hasTasks && (
                    <BaseButton
                        label="Alle löschen"
                        func={() => modalCon?.trigger(DELETE_ALL_TASKS_MODAL_ID)}
                        variant="outline"
                        colorway="warning"
                        size="small"
                    />
                )}
            </div>
            <BaseModal modalId={DELETE_ALL_TASKS_MODAL_ID} title="Alle Aufgaben löschen?" size="small">
                <p className="px-2 pt-4 pb-2">
                    Alle Aufgaben an diesem Objekt werden entfernt und müssen danach neu erzeugt werden.
                </p>
                <div className="flex justify-end gap-2 px-2 pb-4">
                    <BaseButton label="Abbrechen" func={() => modalCon?.trigger(DELETE_ALL_TASKS_MODAL_ID)} variant="ghost" size="small" />
                    <BaseButton
                        label="Löschen"
                        func={() => {
                            deleteAllTasks(props.objectId)
                            modalCon?.trigger(DELETE_ALL_TASKS_MODAL_ID)
                        }}
                        colorway="warning"
                        size="small"
                    />
                </div>
            </BaseModal>
            <div className="max-h-[32rem] overflow-y-auto border-b-2">
                {isLoading && <p className="py-4 text-sm text-gray-400">Laden…</p>}
                {!isLoading && groups.length === 0 && (
                    <p className="p-4 text-sm text-gray-400">Keine Aufgaben. Leistungen im Leistungsverzeichnis anlegen.</p>
                )}
                {groups.map((group) => (
                    <TaskGroupCard
                        key={group.key}
                        group={group}
                        currentWeek={currentWeek}
                        canManage={canManage}
                        onUpdate={updateTask}
                    />
                ))}
            </div>
        </div>
    )
}

type TaskGroupCardType = {
    group: ObjectTaskGroup
    currentWeek: number
    canManage: boolean
    onUpdate: (data: { id: string; status: "done" | "dismissed" | "open" | "rejected"; rejectionComment?: string }) => void
}

function TaskGroupCard(props: TaskGroupCardType) {
    const [showUpcoming, setShowUpcoming] = useState(false)
    const current = props.group.dueNow[0]
    const overdue = Boolean(current && current.dueWeekNumber !== props.currentWeek)

    return (
        <div className="w-full p-4 border-b border-gray-100">
            <div className="flex items-start gap-2">
                <ListTodo className={`${taskIconClass} mt-0.5`} />
                <div className="min-w-0 grow">
                    <p className="text-sm font-semibold" title={props.group.activity}>
                        {props.group.activity}
                    </p>
                    {props.group.description && (
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{props.group.description}</p>
                    )}
                    {current && (
                        <div className="flex flex-wrap items-center gap-1 mt-2">
                            <Tag className="m-0" color={TASK_STATUS_COLOR[current.status]}>
                                {TASK_STATUS_LABEL[current.status]}
                            </Tag>
                            <Tag className="m-0" color={overdue ? "orange" : "default"}>
                                {overdue ? `Überfällig · ${weekLabel(current)}` : `Fällig ${weekLabel(current)}`}
                            </Tag>
                            {props.group.dueNow.length > 1 && (
                                <Tag className="m-0">{props.group.dueNow.length} offene Termine</Tag>
                            )}
                        </div>
                    )}
                    {!current && props.group.upcoming[0] && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            <Tag className="m-0">Nächste Fälligkeit {weekLabel(props.group.upcoming[0])}</Tag>
                        </div>
                    )}
                    {current?.rejectionComment && <p className="text-xs text-gray-500 mt-1">{current.rejectionComment}</p>}
                    {props.group.upcoming.length > 0 && (
                        <button
                            type="button"
                            className="flex items-center gap-1 mt-2 text-xs text-gray-400 hover:text-gray-600"
                            onClick={() => setShowUpcoming((open) => !open)}>
                            {showUpcoming ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            {props.group.upcoming.length === 1 ? "1 weiterer Termin" : `${props.group.upcoming.length} weitere Termine`}
                        </button>
                    )}
                    {showUpcoming && (
                        <ul className="mt-1 pl-1 text-xs text-gray-500">
                            {props.group.upcoming.map((task) => (
                                <li key={task._id} className="py-0.5">
                                    {weekLabel(task)} · {TASK_STATUS_LABEL[task.status]}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {props.canManage && current && current.status !== "done" && current.status !== "dismissed" && (
                    <TaskActions task={current} onUpdate={props.onUpdate} />
                )}
            </div>
        </div>
    )
}

function TaskActions(props: {
    task: TourObjectTask
    onUpdate: TaskGroupCardType["onUpdate"]
}) {
    return (
        <div className="flex items-center gap-1 shrink-0">
            <CircleCheck
                className={`${taskActionIconClass} hover:text-primary`}
                aria-label="Erledigt"
                onClick={() => props.onUpdate({ id: props.task._id, status: "done" })}
            />
            <TourRejectPopover onConfirm={(rejectionComment) => props.onUpdate({ id: props.task._id, status: "rejected", rejectionComment })}>
                <CircleX className={`${taskActionIconClass} hover:text-error`} aria-label="Ablehnen" />
            </TourRejectPopover>
            <TourDeletePopover
                title="Austragen?"
                text="Die Aufgabe wird aus der automatischen Wiedervorlage genommen."
                onConfirm={() => props.onUpdate({ id: props.task._id, status: "dismissed" })}>
                <Trash className={`${taskActionIconClass} hover:text-error`} aria-label="Austragen" />
            </TourDeletePopover>
        </div>
    )
}
